import { useEffect, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import api from "api/axios";
import AbdmServiceBadge from "./AbdmServiceBadge";
import DoctorOrganisationCard from "./DoctorOrganisationCard";
import {
  ABDM_ID_FIELDS,
  ABDM_ID_FIELD_NAMES,
  abdmCheckOutcome,
  abdmCurrentStatus,
  abdmRegistrationRows,
  abdmServiceAlert,
  abdmUnavailableReason,
} from "utils/abdmServiceStatus";

const formatDate = (value, withTime = false) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return withTime
    ? date.toLocaleString("en-IN")
    : date.toLocaleDateString("en-IN");
};

const names = (values) =>
  Array.isArray(values)
    ? values
        .map((value) => value?.name)
        .filter(Boolean)
        .join(", ")
    : "";

const trimmed = (value) => String(value ?? "").trim();

// The HIP ID keeps the test ids the page had when it was the only id checked.
const testId = (field, kind) =>
  field === "hipId"
    ? kind === "outcome"
      ? "abdm-check-outcome"
      : `abdm-service-${kind}`
    : `abdm-${ABDM_ID_FIELDS[field].key}-${kind}`;

/**
 * One doctor, from the doctors list: GET /admin/doctor/:id. The ABDM card runs
 * "Verify ABDM IDs" — POST /admin/doctor/:id/abdm-check, ABDM §3.2.6 for the
 * doctor's HIP ID and HIU ID and the bridge's services list for the HFR ID,
 * now — files the three results and shows the registration the gateway
 * answered. A failing HIP, HIU or HFR verdict notifies the doctor in the
 * doctor app's bell once per (id, verdict).
 */
const DoctorProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [outcomes, setOutcomes] = useState({});

  useEffect(() => {
    let active = true;
    setLoading(true);
    api
      .get(`/admin/doctor/${id}`)
      .then((response) => {
        if (active) setDoctor(response?.data?.data || null);
      })
      .catch((error) => {
        if (active) {
          setDoctor(null);
          toast.error(
            error?.response?.data?.message || "Failed to fetch doctor details"
          );
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id]);

  const verifyAbdmIds = async () => {
    setChecking(true);
    setOutcomes({});
    try {
      const response = await api.post(`/admin/doctor/${id}/abdm-check`);
      const data = response?.data?.data || {};
      // A backend from before the HIU and HFR checks answers the HIP ID's
      // `result` and `abdmServiceStatus` alone.
      const results = data.results || { hipId: data.result };
      setDoctor((prev) => {
        if (!prev) return prev;
        const next = { ...prev };
        for (const field of ABDM_ID_FIELD_NAMES) {
          const { status } = ABDM_ID_FIELDS[field];
          if (status in data) next[status] = data[status] || undefined;
        }
        return next;
      });
      const next = {};
      for (const field of ABDM_ID_FIELD_NAMES) {
        if (results[field]) next[field] = abdmCheckOutcome(results[field], field);
      }
      setOutcomes(next);
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Could not verify the ABDM IDs"
      );
    } finally {
      setChecking(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  // Opened from the list: step back to it, with its tab, page and filters
  // (they live in its URL). Opened from a link: the list's first page.
  const goBack = () =>
    location?.state?.fromList ? navigate(-1) : navigate("/doctors");

  const backButton = (
    <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={goBack}>
      Back
    </Button>
  );

  if (!doctor) {
    return (
      <Box p={3}>
        {backButton}
        <Typography mt={3}>Doctor not found.</Typography>
      </Box>
    );
  }

  const ids = Object.fromEntries(
    ABDM_ID_FIELD_NAMES.map((field) => [field, trimmed(doctor[field])])
  );
  const anyId = ABDM_ID_FIELD_NAMES.some((field) => ids[field]);
  const hfrStatus = abdmCurrentStatus(doctor, "hfrId");
  const linkedServices =
    hfrStatus?.verdict === "ours" && Array.isArray(hfrStatus.status?.linkedServiceIds)
      ? hfrStatus.status.linkedServiceIds.filter(Boolean)
      : [];
  const hipRegistration = abdmRegistrationRows(doctor, "hipId");
  // The same Service ID in both roles has one registration; show it once.
  const hiuRegistration =
    ids.hiuId && ids.hiuId !== ids.hipId ? abdmRegistrationRows(doctor, "hiuId") : [];

  return (
    <Box p={3}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Box display="flex" alignItems="center" gap={2}>
          {backButton}
          <Typography variant="h5" fontWeight={700}>
            {doctor.name || "Doctor"}
          </Typography>
        </Box>
        {doctor.status && (
          <Chip
            label={doctor.status}
            color={doctor.status === "ACTIVE" ? "success" : "default"}
            sx={{ fontWeight: 600 }}
          />
        )}
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: "18px", p: 3, height: "100%" }}>
            <Typography variant="h6" fontWeight={700} mb={2}>
              Basic Information
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <InfoItem label="Email Address" value={doctor.email} />
            <InfoItem label="Phone Number" value={doctor.phone} />
            <InfoItem label="Role" value={doctor.role?.type} />
            <InfoItem label="Hospital / Clinic" value={doctor.hospitalName} />
            <InfoItem label="Joining Date" value={formatDate(doctor.createdAt)} />
            <InfoItem label="Subscription" value={doctor.subscription?.plan?.name} />
            <InfoItem
              label="Subscription Ends"
              value={formatDate(doctor.subscription?.endDate)}
            />
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: "18px", p: 3, height: "100%" }}>
            <Typography variant="h6" fontWeight={700} mb={2}>
              Professional Information
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <InfoItem label="Registration Number" value={doctor.registrationNumber} />
            {/* NHCX pre-auth and claim send it as the practitioner's id; without one they are refused. */}
            <InfoItem label="HPR ID" value={doctor.hprId} testId="doctor-hpr-id" />
            <InfoItem label="Board Name" value={doctor.boardName} />
            <InfoItem label="Experience (years)" value={doctor.experience} />
            <InfoItem label="Speciality" value={names(doctor.speciality)} />
            <InfoItem label="Education" value={names(doctor.education)} />
            <InfoItem label="Department" value={doctor.department} />
          </Card>
        </Grid>

        <Grid item xs={12}>
          <DoctorOrganisationCard
            doctor={doctor}
            onSaved={({ affiliation, phcName }) =>
              setDoctor((prev) => prev && { ...prev, affiliation, phcName })
            }
          />
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ borderRadius: "18px", p: 3 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap={2}
              mb={2}
            >
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  ABDM
                </Typography>
                <Typography fontSize="13px" color="#6b7280">
                  Are the facility ids registered under DRCFO's Bridge ID? The HIP ID and
                  HIU ID are looked up in ABDM; the HFR ID is matched against the
                  bridge's services. A failed check notifies the doctor in the app.
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={
                  checking ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <VerifiedUserIcon />
                  )
                }
                disabled={checking || !anyId}
                onClick={verifyAbdmIds}
                sx={{ textTransform: "none", fontWeight: 600, borderRadius: "10px" }}
              >
                {checking ? "Verifying..." : "Verify ABDM IDs"}
              </Button>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={2}>
              {ABDM_ID_FIELD_NAMES.map((field) => {
                const { label, status: statusKey } = ABDM_ID_FIELDS[field];
                const current = abdmCurrentStatus(doctor, field);
                const checkedAt = current?.status?.checkedAt;
                return (
                  <Grid item xs={12} md={4} key={field} data-testid={testId(field, "card")}>
                    <InfoItem
                      label={field === "hipId" ? "HIP ID (Service ID)" : label}
                      value={ids[field]}
                    />
                    <Box mb={2.5}>
                      <Typography fontSize="13px" color="#6b7280" mb={0.5}>
                        Bridge ID check
                      </Typography>
                      <AbdmServiceBadge doctor={doctor} field={field} />
                    </Box>
                    <InfoItem
                      label="Last Checked"
                      value={
                        ids[field] && checkedAt
                          ? `${formatDate(checkedAt, true)}${
                              current.derived ? " (the HIP ID's check)" : ""
                            }`
                          : ""
                      }
                    />
                    {field === "hfrId" && linkedServices.length > 0 && (
                      <InfoItem
                        label="Services of this facility under DRCFO"
                        value={linkedServices.join(", ")}
                        testId="abdm-hfr-services"
                      />
                    )}
                    {ids[field] && doctor[statusKey]?.lastError &&
                      doctor[statusKey].serviceId === ids[field] && (
                        <Typography fontSize="13px" color="#6b7280" mt={1}>
                          The last check got no answer (
                          {abdmUnavailableReason(doctor[statusKey].lastError)}
                          {doctor[statusKey].lastErrorAt
                            ? `, ${formatDate(doctor[statusKey].lastErrorAt, true)}`
                            : ""}
                          ).
                        </Typography>
                      )}
                  </Grid>
                );
              })}
            </Grid>

            {!anyId && (
              <Typography fontSize="13px" color="#6b7280">
                No ABDM IDs on this profile, so there is nothing to verify.
              </Typography>
            )}
            {ABDM_ID_FIELD_NAMES.map((field) => {
              const alert = abdmServiceAlert(doctor, field);
              return (
                alert && (
                  <Alert
                    key={field}
                    severity={alert.severity}
                    sx={{ mt: 1 }}
                    data-testid={testId(field, "alert")}
                  >
                    <AlertTitle>{alert.title}</AlertTitle>
                    {alert.message}
                  </Alert>
                )
              );
            })}
            {ABDM_ID_FIELD_NAMES.map((field) => {
              const outcome = outcomes[field];
              return (
                outcome && (
                  <Alert
                    key={field}
                    severity={outcome.severity}
                    sx={{ mt: 2 }}
                    onClose={() =>
                      setOutcomes((prev) => {
                        const { [field]: _, ...rest } = prev;
                        return rest;
                      })
                    }
                    data-testid={testId(field, "outcome")}
                  >
                    {outcome.message}
                  </Alert>
                )
              );
            })}
            {hipRegistration.length > 0 && (
              <RegistrationSection
                testId="abdm-registration"
                title="Registration in ABDM"
                intro={`The HIP ID ${ids.hipId}, as the ABDM gateway answered the last check.`}
                rows={hipRegistration}
              />
            )}
            {hiuRegistration.length > 0 && (
              <RegistrationSection
                testId="abdm-hiu-registration"
                title="HIU registration in ABDM"
                intro={`The HIU ID ${ids.hiuId}, as the ABDM gateway answered the last check.`}
                rows={hiuRegistration}
              />
            )}
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

const RegistrationSection = ({ testId: id, title, intro, rows }) => (
  <Box mt={3} data-testid={id}>
    <Divider sx={{ mb: 3 }} />
    <Typography fontWeight={700}>{title}</Typography>
    <Typography fontSize="13px" color="#6b7280" mb={2}>
      {intro} Times are ABDM's own; it gives no time zone.
    </Typography>
    <Grid container spacing={2}>
      {rows.map(({ label, value }) => (
        <Grid item xs={12} sm={4} key={label}>
          <InfoItem label={label} value={value} />
        </Grid>
      ))}
    </Grid>
  </Box>
);

const InfoItem = ({ label, value, testId: id }) => (
  <Box mb={2.5}>
    <Typography fontSize="13px" color="#6b7280" mb={0.5}>
      {label}
    </Typography>
    <Typography fontWeight={600} color="#111827" data-testid={id}>
      {value || value === 0 ? value : "-"}
    </Typography>
  </Box>
);

export default DoctorProfile;
